import { addLinesToCart, formatPrice } from '@theme/lumus-cart';

class LumusComboBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** Picked cards in the order they were picked, so the tray fills left to right. */
  #order = [];

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.picks = [...this.querySelectorAll('[data-combo-item]')];
    this.slots = [...this.querySelectorAll('[data-slots] > li')];
    this.addButton = this.querySelector('[data-add]');
    this.selectAllButton = this.querySelector('[data-select-all-button]');
    this.min = Number(this.dataset.min) || 2;
    this.tiers = (this.dataset.tiers || '')
      .split(',')
      .map((pair) => pair.split(':').map((part) => Number(part.trim())))
      .filter(([quantity, value]) => quantity > 0 && value > 0)
      .map(([quantity, value]) => ({ quantity, value }))
      .sort((a, b) => a.quantity - b.quantity);

    this.#order = this.picks.filter((pick) => !pick.disabled && pick.getAttribute('aria-pressed') === 'true');

    this.picks.forEach((pick) => pick.addEventListener('click', () => this.toggle(pick), { signal }));
    this.selectAllButton?.addEventListener('click', () => this.toggleAll(), { signal });
    this.addButton?.addEventListener('click', () => this.add(), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get selectable() {
    return this.picks.filter((pick) => !pick.disabled);
  }

  /** @param {HTMLButtonElement} pick */
  toggle(pick) {
    if (pick.disabled) return;
    const on = pick.getAttribute('aria-pressed') !== 'true';
    pick.setAttribute('aria-pressed', String(on));
    this.#order = on ? [...this.#order, pick] : this.#order.filter((item) => item !== pick);
    this.render();
  }

  toggleAll() {
    const everything = this.selectable;
    const allPicked = everything.length > 0 && this.#order.length === everything.length;
    everything.forEach((pick) => pick.setAttribute('aria-pressed', String(!allPicked)));
    this.#order = allPicked ? [] : [...this.#order, ...everything.filter((pick) => !this.#order.includes(pick))];
    this.render();
  }

  render({ added = false } = {}) {
    const picked = this.#order;
    const n = picked.length;
    const sum = (key) => picked.reduce((total, pick) => total + Number(pick.dataset[key] || 0), 0);
    const money = (cents) => formatPrice(this, cents);

    const subtotal = sum('price');
    const mrp = sum('compare');
    const tier = n >= this.min ? this.tiers.filter((t) => t.quantity <= n).pop() : undefined;
    const nextTier = this.tiers.find((t) => t.quantity > n && t.quantity >= this.min);
    const offer = tier ? this.#discountFor(tier, subtotal) : 0;
    const price = subtotal - offer;
    const saving = mrp - price;

    this.#text('[data-count]', (this.dataset.countTemplate || '').replace('[selected]', n).replace('[total]', this.picks.length));

    const mrpRow = this.querySelector('[data-mrp-row]');
    if (mrpRow) mrpRow.hidden = n > 0 && mrp <= price;
    this.#text('[data-mrp]', money(mrp));

    const offerRow = this.querySelector('[data-offer-row]');
    if (offerRow) offerRow.hidden = offer <= 0;
    this.#text('[data-offer-amount]', `−${money(offer)}`);
    this.#text('[data-offer-label]', (this.dataset.offerLabel || '').replace('[quantity]', tier?.quantity ?? ''));

    this.#text('[data-combo-price]', money(price));
    this.#text(
      '[data-save]',
      n === 0 ? this.dataset.emptySave || '' : saving > 0 ? (this.dataset.saveTemplate || '').replace('[amount]', money(saving)) : ''
    );

    this.slots.forEach((slot, index) => {
      const pick = picked[index];
      const source = pick?.querySelector('img, svg');
      slot.replaceChildren();
      slot.classList.toggle('lumus-combo__slot--photo', Boolean(pick?.classList.contains('lumus-pick--photo')));
      if (source) {
        const thumb = /** @type {Element} */ (source.cloneNode(true));
        if (thumb instanceof HTMLImageElement) {
          thumb.sizes = '84px';
          thumb.loading = 'eager';
        }
        thumb.removeAttribute('class');
        slot.append(thumb);
      } else {
        const plus = document.createElement('span');
        plus.className = 'lumus-combo__slot-plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        slot.append(plus);
      }
    });

    if (this.addButton) {
      this.addButton.disabled = n < this.min;
      this.addButton.textContent = added ? this.dataset.ctaAdded || '' : this.dataset.cta || '';
    }

    const hint = this.querySelector('[data-hint]');
    if (hint) {
      hint.classList.remove('lumus-combo__hint--error');
      if (n < this.min) {
        hint.textContent = (this.dataset.hintShort || '').replace('[min]', this.min);
      } else if (nextTier && nextTier.quantity <= this.selectable.length && this.dataset.hintNext) {
        const nextSubtotal = subtotal + Number(this.selectable.find((pick) => !picked.includes(pick))?.dataset.price || 0) * (nextTier.quantity - n);
        hint.textContent = this.dataset.hintNext
          .replace('[count]', nextTier.quantity - n)
          .replace('[amount]', money(this.#discountFor(nextTier, nextSubtotal)));
      } else {
        hint.textContent = this.dataset.hintReady || '';
      }
    }

    if (this.selectAllButton) {
      const total = this.selectable.length;
      this.selectAllButton.textContent =
        total > 0 && n === total ? this.dataset.clear || '' : (this.dataset.selectAll || '').replace('[count]', total);
    }
  }

  async add() {
    const button = this.addButton;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    if (this.#order.length < this.min) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      await addLinesToCart(
        this,
        this.#order.map((pick) => ({ id: pick.dataset.variantId, quantity: 1 }))
      );
      this.render({ added: true });
    } catch (error) {
      this.render();
      const hint = this.querySelector('[data-hint]');
      if (hint) {
        hint.textContent = error instanceof Error ? error.message : 'Could not add your combo. Please try again.';
        hint.classList.add('lumus-combo__hint--error');
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** Discount in minor units for a tier; tier values are in major units (₹) or percent. */
  #discountFor(tier, subtotal) {
    const discount =
      this.dataset.discountType === 'percent' ? Math.round((subtotal * tier.value) / 100) : Math.round(tier.value * 100);
    return Math.min(discount, subtotal);
  }

  /** @param {string} selector @param {string} value */
  #text(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value;
  }
}

if (!customElements.get('lumus-combo-builder')) customElements.define('lumus-combo-builder', LumusComboBuilder);
